// Что показывает кнопка «Загрузить» в «Скауте».
//
// Кнопка ничего не спрашивает — то же решение, что на «Моделях»: человек нажал,
// значит просил. Но раз она молчит ДО прогона, она обязана говорить ВО ВРЕМЯ и
// ПОСЛЕ: у прогона есть потолок, и о недобранном молчать нельзя.
//
// Сцен над рамкой за месяц бывают десятки, в прогон берётся шесть. Молча взяв
// первые, мы отдали бы неполный осмотр за полный: карта после прогона выглядит
// одинаково и в том, и в другом случае.
//
// Слова подставляет компонент — здесь только ключи и числа.

export interface ScanJob {
  job_id: number
  status: 'queued' | 'running' | 'done' | 'failed'
  phase: string | null
  percent: number
  /** Сколько сцен ЕСТЬ над рамкой, и сколько взято в прогон. */
  scenes_found: number
  scenes_total: number
  scenes_done: number
  marks: number
  error: string | null
  /** Осмотр одного снимка календаря: `источник:номер`. */
  scene_key?: string | null
}

export interface Msg {
  key: string
  values?: Record<string, string | number>
}

export interface ScanView {
  busy: boolean
  failed: boolean
  percent: number
  /** Главная строка: где прогон или чем кончился. */
  line: Msg | null
  /** Оговорка о недобранном. `null` — осмотрено всё, что было. */
  note: Msg | null
}

/** Как часто спрашивать ход. Прогон живёт минуты: полоска, оживающая раз в
 *  минуту, читается как зависшая, а раз в секунду — это лишний запрос на
 *  каждую отрисовку. */
export const POLL_MS = 3000

const PHASES = new Set(['search', 'optical', 'radar'])

export function scanView(job: ScanJob | null | undefined): ScanView {
  if (!job) return { busy: false, failed: false, percent: 0, line: null, note: null }

  const busy = job.status === 'queued' || job.status === 'running'
  const failed = job.status === 'failed'

  let line: Msg | null = null
  if (failed) {
    // Отказ без причины неотличим от поломки самой кнопки.
    line = { key: 'imagery.scan.failed', values: { error: job.error ?? '' } }
  } else if (busy) {
    const phase = job.phase && PHASES.has(job.phase) ? job.phase : 'search'
    line = { key: `imagery.scan.phase.${phase}`,
             values: { done: job.scenes_done, total: job.scenes_total,
                       marks: job.marks } }
  } else if (job.scenes_total === 0) {
    // «Снимков за окно нет» — это ОТВЕТ. Пустая полоска на его месте читалась
    // бы как несработавшая кнопка.
    line = { key: 'imagery.scan.empty' }
  } else {
    line = { key: 'imagery.scan.done',
             values: { scenes: job.scenes_done, marks: job.marks } }
  }

  const capped = job.scenes_found > job.scenes_total && job.scenes_total > 0
  const note = capped
    ? { key: 'imagery.scan.capped',
        values: { total: job.scenes_total, found: job.scenes_found } }
    : null

  return { busy, failed, percent: Math.max(0, Math.min(100, job.percent)), line, note }
}
